import type { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";

export function esFechaISO(fecha: string): boolean {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(fecha);
  if (!parts) return false;
  const [, y, m, d] = parts.map(Number);
  const days = [31, y % 4 === 0 && (y % 100 !== 0 || y % 400 === 0) ? 29 : 28,
    31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return y >= 1 && m >= 1 && m <= 12 && d >= 1 && d <= days[m - 1];
}

export function esFechaFormato(fecha: string): boolean {
  return /^(\d{2})\/(\d{2})\/(\d{4})$/.test(fecha) && esFechaISO(fecha.split('/').reverse().join('-'));
}

/** Parse a calendar date at its source boundary, without guessing locale or using timezones. */
export function normalizarFechaCalendario(
  valor: string | null | undefined,
  formato: 'YYYY-MM-DD' | 'DD/MM/YYYY' | 'MM/DD/YYYY'
): string | null {
  if (valor == null || valor.trim() === '') return null;
  const texto = valor.trim();
  if (esFechaISO(texto)) return texto;
  const partes = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(texto);
  if (partes && formato !== 'YYYY-MM-DD') {
    const [, primera, segunda, anio] = partes;
    const iso = formato === 'MM/DD/YYYY'
      ? `${anio}-${primera}-${segunda}` : `${anio}-${segunda}-${primera}`;
    if (esFechaISO(iso)) return iso;
  }
  throw new RangeError(`Invalid calendar date "${texto}"; expected ${formato}.`);
}

export function esFechaString(fecha: string): boolean {
  return esFechaISO(fecha) || esFechaFormato(fecha);
}


/**
 * Transforma las fechas de un objeto del formato <yyyy-MM-dd> al formato <dd/MM/yyyy>.
 * @param obj Objeto con fechas en formato <yyyy-MM-dd>.
 * @returns Objeto con fechas en formato <dd/MM/yyyy>.
 */
export function transformarFechasISOAFormato<Data extends object>(obj: any): Data {
  if (obj instanceof Date) return dateToISO(obj) as unknown as Data;
  if (obj === null || typeof obj !== 'object') return obj;
  if (!Array.isArray(obj) && Object.getPrototypeOf(obj) !== Object.prototype) return obj;
  const fechaFormatoRegex = /^(\d{4})-(\d{2})-(\d{2})$/;
  const resultado: any = Array.isArray(obj) ? [] : {};

  for (const key in obj) {
    if (!obj.hasOwnProperty(key)) continue;
    const valor = obj[key];
    if (typeof valor === 'string' && fechaFormatoRegex.test(valor)) {
      const match = valor.match(fechaFormatoRegex);
      if (match) {
        const [, anio, mes, dia] = match;
        resultado[key] = `${dia}/${mes}/${anio}`;
      } else {
        resultado[key] = valor;
      }
    } else if (typeof valor === 'object' && valor !== null) {
      resultado[key] = transformarFechasISOAFormato(valor);
    } else {
      resultado[key] = valor;
    }
  }
  return resultado as Data;
}

/**
 * Transforma las fechas de un objeto del formato <dd/MM/yyyy> al formato <yyyy-MM-dd>.
 * @param obj Objeto con fechas en formato <dd/MM/yyyy>.
 * @returns Objeto con fechas en formato <yyyy-MM-dd>.
 */
export function transformarFechasFormatoAISO<Data extends object>(obj: any): Data {
  if (obj instanceof Date) return dateToISO(obj) as unknown as Data;
  if (obj === null || typeof obj !== 'object') return obj;
  if (!Array.isArray(obj) && Object.getPrototypeOf(obj) !== Object.prototype) return obj;
  const fechaFormatoRegex = /^\d{2}\/\d{2}\/\d{4}$/;
  const resultado: any = Array.isArray(obj) ? [] : {};

  for (const key in obj) {
    if (!obj.hasOwnProperty(key)) continue;
    const valor = obj[key];
    if (typeof valor === 'string' && fechaFormatoRegex.test(valor) && esFechaFormato(valor)) {
      resultado[key] = normalizarFechaCalendario(valor, 'DD/MM/YYYY');

    } else if (typeof valor === 'object' && valor !== null) {
      resultado[key] = transformarFechasFormatoAISO(valor);
    } else {
      resultado[key] = valor;
    }
  }
  return resultado as Data;
}

export function dateToISO(date: Date): string {
  if (!(date instanceof Date) || !Number.isFinite(date.getTime())) {
    throw new RangeError('Invalid calendar date');
  }
  return [String(date.getFullYear()).padStart(4, '0'), String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0')].join('-');
}

export function dateToFormato(date: Date): string {
  return dateToISO(date).split('-').reverse().join('/');
}

export function hoyISO(): string {
  return fechaHoraServidorISO().slice(0, 10);
}

export function hoyFormato(): string {
  return isoAFormato(hoyISO());
}

/**
 * Transforma una fecha en formato yyyy-MM-dd a dd/MM/yyyy.
 * @param fechaISO Fecha en formato <yyyy-MM-dd>.
 * @returns Fecha en formato <dd/MM/yyyy>.
 */
export function isoAFormato(fechaISO: string): string {
  if (!fechaISO) return fechaISO;
  const fechaISORegex = /^(\d{4})-(\d{2})-(\d{2})/;
  const match = fechaISO.match(fechaISORegex);
  if (!match) return fechaISO;

  const [, anio, mes, dia] = match;
  return `${dia}/${mes}/${anio}`;
}

/**
 * Transforma una fecha en formato dd/MM/yyyy a yyyy-MM-dd.
 * @param fechaFormato Fecha en formato <dd/MM/yyyy>.
 * @returns Fecha en formato <yyyy-MM-dd>.
 */
export function formatoAISO(fechaFormato: string, eeuu: boolean = false): string {
  if (!fechaFormato) return fechaFormato;
  try {
    return normalizarFechaCalendario(fechaFormato, eeuu ? 'MM/DD/YYYY' : 'DD/MM/YYYY') ?? fechaFormato;
  } catch {
    // Legacy display callers preserve unrecognized values; imports use the strict parser directly.
    return fechaFormato;
  }
}

export type UnidadTiempo = 'dias' | 'meses' | 'años';

/**
 * Suma o resta unidades de tiempo a una fecha en formato dd/MM/yyyy, sin usar Date.
 * @param fechaStr Fecha en formato string.
 * @param cantidad Número de unidades a sumar (positivo) o restar (negativo).
 * @param unidad Unidad de tiempo: 'dias', 'meses', 'anios'.
 * @returns Nueva fecha en el mismo formato que la original.
 */
export function sumarRestarFechaString(fechaStr: string, cantidad: number, unidad: UnidadTiempo): string {
  const fechaFormatoRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
  const match = fechaStr.match(fechaFormatoRegex);
  if (!match) return fechaStr;

  let [, d, m, y] = match;
  let dia = parseInt(d, 10);
  let mes = parseInt(m, 10);
  let anio = parseInt(y, 10);

  const esBisiesto = (a: number) => a % 4 === 0 && (a % 100 !== 0 || a % 400 === 0);
  const diasMes = (a: number, m: number) =>
    [31, esBisiesto(a) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][m - 1];

  switch (unidad) {
    case 'dias':
      dia += cantidad;
      while (dia > diasMes(anio, mes)) {
        dia -= diasMes(anio, mes);
        mes++;
        if (mes > 12) {
          mes = 1;
          anio++;
        }
      }
      while (dia < 1) {
        mes--;
        if (mes < 1) {
          mes = 12;
          anio--;
        }
        dia += diasMes(anio, mes);
      }
      break;
    case 'meses':
      mes += cantidad;
      while (mes > 12) {
        mes -= 12;
        anio++;
      }
      while (mes < 1) {
        mes += 12;
        anio--;
      }
      if (dia > diasMes(anio, mes)) dia = diasMes(anio, mes);
      break;
    case 'años':
      anio += cantidad;
      if (mes === 2 && dia > diasMes(anio, mes)) dia = diasMes(anio, mes);
      break;
  }

  const diaStr = String(dia).padStart(2, '0');
  const mesStr = String(mes).padStart(2, '0');
  const anioStr = String(anio);

  return `${diaStr}/${mesStr}/${anioStr}`;
}

export function normalizarFecha(fecha: string): string {
  if (esFechaISO(fecha)) {
    return fecha; // Ya está en formato ISO
  }

  if (esFechaFormato(fecha)) {
    // Convertir dd/MM/yyyy a yyyy-MM-dd
    const [dia, mes, anio] = fecha.split('/');
    return `${anio}-${mes}-${dia}`;
  }

  return null; // si no es una fecha valida, devuelve null
};

/**
 * Compara dos fechas y devuelve true si la fecha1 es mayor que la fecha2.
 * @param fecha1 Fecha en formato dd/MM/yyyy o yyyy-MM-dd.
 * @param fecha2 Fecha en formato dd/MM/yyyy o yyyy-MM-dd.
 * @returns true si la fecha1 es mayor que la fecha2, false en caso contrario.
 */
export function fechaMayorQueFecha(fecha1: string, fecha2: string): boolean {
  if (!fecha1 || !fecha2) return false; // si no hay fecha, devuelve false

  // Normalizar fechas a formato ISO para comparación
  const fecha1Normalizada = normalizarFecha(fecha1);
  if (!fecha1Normalizada) return false; // si no es una fecha valida, devuelve false

  const fecha2Normalizada = normalizarFecha(fecha2);
  if (!fecha2Normalizada) return false; // si no es una fecha valida, devuelve false

  // Comparar como strings (funciona porque yyyy-MM-dd es comparable lexicográficamente)
  return fecha1Normalizada > fecha2Normalizada;
}

/**
 * Valida que la fecha de inicio sea menor que la fecha de fin en un FormGroup.
 * @param group FormGroup con las fechas de inicio y fin.
 * @returns ValidationErrors | null.
 */
export function rangoFechasValido(campoInicio: string, campoFin: string): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const inicio = group.get(campoInicio)?.value;
    const fin = group.get(campoFin)?.value;
    if (fechaMayorQueFecha(inicio, fin)) {
      return { rangoFechasInvalido: true };
    }
    return null;
  };
}

export function tiempoEntreFechas(fechaInicio: string, fechaFin: string, unidad: UnidadTiempo): number {
  const fechaInicioDate = new Date(normalizarFecha(fechaInicio));
  const fechaFinDate = new Date(normalizarFecha(fechaFin));
  const tiempo = fechaFinDate.getTime() - fechaInicioDate.getTime();

  switch (unidad) {
    case 'dias':
      return tiempo / (1000 * 60 * 60 * 24);
    case 'meses':
      return tiempo / (1000 * 60 * 60 * 24 * 30);
    case 'años':
      return tiempo / (1000 * 60 * 60 * 24 * 365);
  }
}

export function fechaLargaEspanol(fecha: string): string {
  if (!fecha) return '';
  // Normalizar a formato ISO
  let iso = '';
  if (esFechaFormato(fecha)) {
    // dd/MM/yyyy
    const [dia, mes, anio] = fecha.split('/');
    iso = `${anio}-${mes}-${dia}`;
  } else if (esFechaISO(fecha)) {
    // yyyy-MM-dd
    iso = fecha;
  } else {
    return fecha; // formato no reconocido
  }

  const meses = [
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
  ];

  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return fecha;

  const [, anio, mes, dia] = match;
  const mesNombre = meses[parseInt(mes, 10) - 1];
  // Eliminar ceros a la izquierda del día
  const diaNum = parseInt(dia, 10);

  return `${diaNum} de ${mesNombre} del ${anio}`;
}

/**
 * Abreviaturas de mes en inglés para el formato de visualización DD-M-YYYY (ej: 01-May-2029).
 */
const MESES_ABREVIADOS_INGLES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
] as const;

/**
 * Indica si una cadena cumple el patrón DD-M-YYYY con mes abreviado en inglés.
 *
 * Propósito: validar fechas mostradas en calendarios y tablas del módulo de discrepancias.
 *
 * Flujo: aplica una expresión regular estricta sobre la cadena recibida.
 *
 * @param fecha Cadena a evaluar (ej: `01-May-2029`).
 * @returns `true` si coincide con el formato esperado.
 */
export function esFechaNuevoFormato(fecha: string): boolean {
  return /^\d{2}-[A-Za-z]{3}-\d{4}$/.test(fecha);
}

/**
 * Transforma una fecha ISO (o dd/MM/yyyy) al formato DD-M-YYYY para la UI.
 *
 * Propósito: presentar fechas en tablas y `p-calendar` con mes abreviado en inglés
 * mientras el backend sigue usando ISO.
 *
 * Flujo:
 * 1) Normaliza la entrada eliminando espacios.
 * 2) Si detecta prefijo `yyyy-MM-dd` (con o sin hora), arma día-mes-año abreviado.
 * 3) Si es `dd/MM/yyyy`, la convierte vía `formatoAISO` y repite el paso 2.
 * 4) Si no reconoce el formato, devuelve el valor original.
 *
 * @param fechaISO Fecha en ISO, ISO con hora o `dd/MM/yyyy`.
 * @returns Fecha en formato `DD-M-YYYY` o la cadena original si no es parseable.
 */
export function isoANuevoFormato(fechaISO: string): string {
  if (!fechaISO) {
    return fechaISO;
  }

  const texto = String(fechaISO).trim();
  let iso = '';

  const coincidenciaIso = texto.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (coincidenciaIso) {
    iso = `${coincidenciaIso[1]}-${coincidenciaIso[2]}-${coincidenciaIso[3]}`;
  } else if (esFechaFormato(texto)) {
    iso = formatoAISO(texto, false);
  } else {
    return fechaISO;
  }

  const partes = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!partes) {
    return fechaISO;
  }

  const [, anio, mes, dia] = partes;
  const indiceMes = parseInt(mes, 10) - 1;
  if (indiceMes < 0 || indiceMes > 11) {
    return fechaISO;
  }

  return `${dia}-${MESES_ABREVIADOS_INGLES[indiceMes]}-${anio}`;
}

/**
 * Transforma una fecha DD-M-YYYY al formato ISO `yyyy-MM-dd` para envío al backend.
 *
 * Propósito: convertir el valor string del `p-calendar` antes de armar query params o body.
 *
 * Flujo:
 * 1) Si la cadena ya es ISO válida, devuelve solo la parte de fecha (10 caracteres).
 * 2) Si coincide con DD-M-YYYY, resuelve el mes abreviado y compone `yyyy-MM-dd`.
 * 3) Si no reconoce el formato, devuelve el valor original.
 *
 * @param fecha Fecha en formato `DD-M-YYYY` o ISO.
 * @returns Fecha en `yyyy-MM-dd` o la cadena original si no es convertible.
 */
export function nuevoFormatoAISO(fecha: string): string {
  if (!fecha) {
    return fecha;
  }

  const texto = String(fecha).trim();

  if (esFechaISO(texto)) {
    return texto;
  }

  const coincidencia = texto.match(/^(\d{2})-([A-Za-z]{3})-(\d{4})$/);
  if (!coincidencia) {
    return fecha;
  }

  const [, dia, mesAbreviado, anio] = coincidencia;
  const indiceMes = MESES_ABREVIADOS_INGLES.findIndex(
    (mes) => mes.toLowerCase() === mesAbreviado.toLowerCase()
  );

  if (indiceMes === -1) {
    return fecha;
  }

  const mes = String(indiceMes + 1).padStart(2, '0');
  return `${anio}-${mes}-${dia}`;
}

/** One application clock, supplied by CakePHP; never infer the server's zone from the browser. */
let relojServidor: { epoch: number; sampledAt: number; timeZone: string } | null = null;

export function sincronizarRelojServidor(serverTime: string, timeZone: string): void {
  if (!/^\d{4}-\d{2}-\d{2}T.*(?:Z|[+-]\d{2}:\d{2})$/.test(serverTime)) {
    throw new RangeError('Server time must include its UTC offset');
  }
  const epoch = Date.parse(serverTime);
  if (!Number.isFinite(epoch)) throw new RangeError('Invalid server time');
  new Intl.DateTimeFormat('en-GB', { timeZone }).format(epoch);
  relojServidor = { epoch, sampledAt: performance.now(), timeZone };
}

/** Server wall time in an unambiguous fixed representation, without browser locale. */
export function fechaHoraServidorISO(instant?: number): string {
  if (!relojServidor) throw new Error('Server clock has not been initialized');
  const epoch = instant ?? relojServidor.epoch + performance.now() - relojServidor.sampledAt;
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: relojServidor.timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
  }).formatToParts(epoch);
  const part = (name: string) => parts.find(p => p.type === name)!.value;
  return part('year') + '-' + part('month') + '-' + part('day') + 'T' +
    part('hour') + ':' + part('minute') + ':' + part('second');
}

/** A Date used ONLY as a calendar widget carrier, not as an instant. */
export function fechaParaCalendario(value: string | Date): Date {
  if (value instanceof Date) {
    if (!Number.isFinite(value.getTime())) throw new RangeError('Invalid calendar date');
    return new Date(value.getTime());
  }
  const iso = normalizarFecha(value);
  if (!iso || !esFechaISO(iso)) throw new RangeError('Expected a calendar date');
  const [year, month, day] = iso.split('-').map(Number);
  const result = new Date(year, month - 1, day, 12);
  result.setFullYear(year);
  if (dateToISO(result) !== iso) throw new RangeError('Invalid calendar date');
  return result;
}

/** Excel serials are calendar values, not browser-local instants. Day/month is explicit. */
export function fechaExcelISO(value: unknown): string | null {
  if (value instanceof Date) return Number.isFinite(value.getTime()) ? dateToISO(value) : null;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;
    const carrier = new Date(Math.floor(value - 25569) * 86400000);
    if (!Number.isFinite(carrier.getTime())) return null;
    const iso = carrier.toISOString().slice(0, 10);
    return esFechaISO(iso) ? iso : null;
  }
  if (typeof value !== 'string' || !value.trim()) return null;
  const text = value.trim();
  const iso = fechaCalendarioISO(nuevoFormatoAISO(text));
  if (iso) return iso;
  const parts = /^(\d{1,2})[\/.-](\d{1,2})(?:[\/.-](\d{2}|\d{4}))?$/.exec(text);
  if (!parts) return null;
  const year = parts[3] ? (parts[3].length === 2 ? '20' + parts[3] : parts[3]) : hoyISO().slice(0, 4);
  const result = year + '-' + parts[2].padStart(2, '0') + '-' + parts[1].padStart(2, '0');
  return esFechaISO(result) ? result : null;
}

export function hoyParaCalendario(): Date {
  return fechaParaCalendario(hoyISO());
}

/** Extract a stored calendar day without timezone conversion or ambiguous native parsing. */
export function fechaCalendarioISO(value: string): string | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  const iso = normalizarFecha(text) ?? (/^\d{4}-\d{2}-\d{2}[T ]/.test(text) ? text.slice(0, 10) : null);
  if (!iso) return null;
  try { fechaParaCalendario(iso); return iso; } catch { return null; }
}

/** Synthetic UTC carrier for formatting. Stored server strings retain their written wall time. */
export function fechaParaMostrar(value: string | number | Date): Date | null {
  let text: string;
  if (value instanceof Date) {
    if (!Number.isFinite(value.getTime())) return null;
    text = dateToISO(value) + 'T' + [value.getHours(), value.getMinutes(), value.getSeconds()]
      .map(n => String(n).padStart(2, '0')).join(':');
  } else if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;
    text = fechaHoraServidorISO(value);
  } else {
    text = String(value).trim();
    if (/^\d{11,}$/.test(text)) text = fechaHoraServidorISO(Number(text));
    if (esFechaFormato(text)) text = formatoAISO(text);
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/.exec(text);
  if (!match) return null;
  const [, year, month, day, hour = '00', minute = '00', second = '00'] = match;
  const result = new Date(0);
  result.setUTCFullYear(+year, +month - 1, +day);
  result.setUTCHours(+hour, +minute, +second, 0);
  if (result.getUTCFullYear() !== +year || result.getUTCMonth() !== +month - 1 ||
      result.getUTCDate() !== +day || +hour > 23 || +minute > 59 || +second > 59) return null;
  return result;
}
